import { Circ, Elastic, Expo, Sine, TimelineMax, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { PlayMode } from "html-living-framework";
import { isMobile } from "html-living-framework/lib/Utils/PlatformUtils";
import * as PIXI from "pixi.js";
import { Application } from "../Application";
import { Ease } from "../core/Animatable";
import { Container } from "../core/Container";
import { PointUtil } from "../core/Geom";
import { array, Mix } from "../core/Reflection";
import { Sprite } from "../core/Sprite";
import { Subscriber } from "../core/Subscriber";
import { CaptionToken } from "../data/CaptionToken";
import LightsOutGameModel from "../data/LightsOutGameModel";
import * as PlanterGameModel from "../data/PlanterGameModel";
import TrashGameModel from "../data/TrashGameModel";
import { TrashType } from "../data/TrashType";
import * as VO from "../data/VO";
import { randomExhaust } from "../data/VO";
import { Button, OutlinedButton } from "../gameobjects/Button";
import { Oscar } from "../gameobjects/Oscar";
import { OscarReturn } from "../gameobjects/OscarReturn";
import { TrashBin } from "../gameobjects/TrashBin";
import { TrashItem } from "../gameobjects/TrashItem";
import { TrashItemReturn } from "../gameobjects/TrashItemReturn";
import { TrashReturnLayout_hud, TrashReturnLayout_main } from "../layout/TrashReturnLayout";
import { CaptionManager } from "../managers/CaptionManager";
import { TrashBadgePopup } from "../popups/TrashBadgePopup";
import { AbstractGameState } from "./AbstractGameState";

const correctAnswerVOs = randomExhaust([VO.TB_SORT09, VO.TB_SORT10, VO.TB_SORT11, (item: TrashItem) => {
    const ret = [item.voiceoverA];
    switch (item.trashType) {
        case TrashType.Paper:
            ret.push(VO.TB_SORT08);
            break;
        case TrashType.Plastic:
            ret.push(VO.TB_SORT08B);
            break;
        case TrashType.Metal:
            ret.push(VO.TB_SORT08C);
            break;
        case TrashType.Organic:
            ret.push(VO.TB_SORT08E);
            break;
    }
    return ret;
}]);
const oscarReactVO = [VO.TB_COLLECT09, VO.TB_COLLECT10];
const clickVO = isMobile() ? VO.TB_PROMPT01 : VO.TB_PROMPT02;
const idleVOs = randomExhaust([(item: TrashItem) => [VO.TB_SORT_TO01, item.voiceoverC], [clickVO, VO.TB_SORT_TO02]] );
HLF.ArrayUtils.shuffle(oscarReactVO);

export class TrashReturnScreen extends AbstractGameState {

    public static allItemsReturnedVO = randomExhaust([VO.TB_SORT12, VO.TB_SORT13]);

    public static readonly NAME = "TrashReturnScreen";

    public readonly main = this.addChild(new Container());
    public readonly parkBackground = this.main.addChild(new Sprite());
    public readonly parkContainer = this.main.addChild(new Container());
    public readonly bins = (([
        [TrashType.Paper, VO.TB_RECYCLE01],
        [TrashType.Organic, VO.TB_RECYCLE05],
        [[TrashType.Plastic, TrashType.Metal], VO.TB_RECYCLE02],
    ]) as Array<[TrashType | TrashType[], string]>).map((it) => this.parkContainer.addChild(new TrashBin(it[0], it[1])));
    public readonly oscar = this.parkContainer.addChild(new OscarReturn());
    public readonly throwContainer = this.main.addChild(new Container());

    public readonly hud = this.addChild(new Container());
    public readonly homeButton = this.hud.addChild(new OutlinedButton());

    protected itemsReturned: number = 0;
    protected wrongAnswerCounter: number = 0;
    protected correctAnswerCounter: number = 0;
    protected readonly _subscriptions: any[] = [];
    protected analyticsScreenName: string = "4b Trash Blast_Sort Trash Screen";
    protected analyticsRoundName = "Trash Blast";
    protected screenStartTime: number = 0;

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
        const bgm = HLF.Application.instance.audio.getAudioTrack("trash_blast_loop", HLF.AudioCategory.BGM) ?? HLF.Application.instance.audio.createAudioTrack("trash_blast_loop", HLF.AudioCategory.BGM);
        if (bgm.isPlaying() === false) {
            bgm.setLooped(true);
            bgm.play();
        }
    }

    public init(pSize: PIXI.Point) {
        // can't actually pass game objects between scenes, so we recreate them
        this.oscar.collectedItems = TrashGameModel.collectedItems.map((it) => this.parkContainer.addChild(new TrashItemReturn(it.trashType, it.name, it.voiceoverA, it.voiceoverB, it.voiceoverC)));

        TrashReturnLayout_main.call(this);
        this.main.scale.set(0.5);

        TrashReturnLayout_hud.call(this);
        this.hud.scale.set(0.5);

        this.homeButton.onClick.add(() => {
            this.trackEvent("gameMenuClick", {buttonName: "Home"});
            this.gotoNextState("MainMenu");
        });
        this.homeButton.clickSfx = "buttonclick";

        this.interactive = false;
        this.bins.forEach((b) => b.interactive = false);
        this.startSorting();

        this._subscriptions.push(PubSub.subscribe("oscar aim", (pTopic: string, pData: "left" | "right" | "center" | "none") => {
            if (!this.oscar.loadedItem) {
                return;
            }

            switch (pData) {
                case "left":
                    this.bins[0].Highlight();
                    this.bins[1].UnHighlight();
                    this.bins[2].UnHighlight();
                    break;
                case "center":
                    this.bins[0].UnHighlight();
                    this.bins[1].Highlight();
                    this.bins[2].UnHighlight();
                    break;
                case "right":
                    this.bins[0].UnHighlight();
                    this.bins[1].UnHighlight();
                    this.bins[2].Highlight();
                    break;
                case "none":
                    this.bins[0].UnHighlight();
                    this.bins[1].UnHighlight();
                    this.bins[2].UnHighlight();
                    break;
            }
        }));

        this.oscar.spine.addListener(HLF.Input.Events.POINTER_TAP, (e: PIXI.InteractionEvent) => {
            console.log("oscar clicked", e);
            this.idleTimeline?.restart(true);
            if (this.idleStartTime !== undefined) {
                const idleEndTime = new Date().valueOf();
                const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
                this.trackEvent("gameTOReEngage", {duration: idleDuration});
                this.idleStartTime = undefined;
            }
            if (e.stopped) { return; }
            const voToPlay = oscarReactVO[0];
            // cycle vo only if it actually played
            const onVoDidPlay = (_pTopic: string, _pData: string) => {
                this.oscar.Smile();
                if (_pData === voToPlay) {
                    if (oscarReactVO[0] === voToPlay) {
                        oscarReactVO.push(oscarReactVO.shift()!);
                    }
                }
                PubSub.unsubscribe(token);
            };

            const token = PubSub.subscribe(HLF.VOICEOVER_STARTED, onVoDidPlay);
            HLF.Application.instance.voiceover.playVO(voToPlay!, HLF.PlayMode.New, () => {
                PubSub.unsubscribe(token);
                this.idleTimeline?.restart(true);
            });
        });

        this.oscar.scaleXY *= 0.88;

        this.trackEvent("gameComponentStart", {
            componentType: "Sort",
            componentName: "Sort Trash",
        });
        this.screenStartTime = new Date().valueOf();
        super.init(pSize);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.hud.position.set(-pSize.x / 2, -pSize.y / 2);
        this.homeButton.x = 108 * 2;
        this.homeButton.y = 134.5 * 2;
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
        this.oscar.update(pDeltaTime);
    }

    public destroy() {
        super.destroy();
        this._subscriptions.forEach(PubSub.unsubscribe);
    }

    protected onTouchUp(e: PIXI.InteractionEvent) {
        this.idleTimeline?.restart(true);
        if (this.idleStartTime !== undefined) {
            const idleEndTime = new Date().valueOf();
            const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
            this.trackEvent("gameTOReEngage", {duration: idleDuration});
            this.idleStartTime = undefined;
        }
        for (let i = 0; i < this.bins.length; i++) {
            if (this.bins[i].highlighted) {
                this.throwTo(this.bins[i]);
                break;
            }
        }
    }

    protected onBadgePopupClosed() {
        this.oscar.visible = true;
    }
/*
    private onSwipe(pTopic: string, pData: PIXI.Point) {

        const direction = PointUtil.normalize(pData);

        if (direction.y < 0) {
            if (Math.abs(direction.x) < 0.5) {
                // swipe up
                this.bins[1].onClick(this.bins[1], undefined);
            }
            else if (direction.x < 0) {
                // swipe up and left
                this.bins[0].onClick(this.bins[0], undefined);
            }
            else {
                // swipe up and right
                this.bins[2].onClick(this.bins[2], undefined);
            }
        }
        else {
            if (Math.abs(direction.x) < 0.5) {
                // swipe down
            }
            else if (direction.x < 0) {
                // swipe down and left
            }
            else {
                // swipe down and right
            }
        }
    }
 */
    private startSorting() {
        let d = 0;
        for (const bin of this.bins) {
            bin.visible = true;
            bin.moveYFrom(this._size.y + bin.height, 0.4, Ease.backOut, { delay: d += 0.2 });
            bin.onClick.add(() => {
                this.idleTimeline?.restart(true);
                if (this.idleStartTime !== undefined) {
                    const idleEndTime = new Date().valueOf();
                    const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
                    this.trackEvent("gameTOReEngage", {duration: idleDuration});
                    this.idleStartTime = undefined;
                }
                this.throwTo(bin);
            });
        }

        const _startSorting = () => {
            HLF.Application.instance.voiceover.playVO([0.5, VO.TB_SORT05], () => {
                this.interactive = true;
                this.bins.forEach((b) => b.interactive = true);
                this.addListener(HLF.Input.Events.TOUCH_END, this.onTouchUp.bind(this));
                this.addListener(HLF.Input.Events.CLICK, this.onTouchUp.bind(this));

                this.oscar.spine.interactive = true;
                this.oscar.spine.buttonMode = true;

                this.oscar.loadItem();

                this.idleTimeline.pause();
                this.idleTimeline.call(() => {
                    this.trackEvent("gameTimeoutPlay");
                    if (this.idleStartTime === undefined) {
                        this.idleStartTime = new Date().valueOf();
                    }
                    const nextVO = idleVOs();
                    let idleVO: string | string[];
                    if (nextVO instanceof Function) {
                        idleVO = nextVO(this.oscar.loadedItem!);
                        PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(idleVO[0], 0, {trashc: idleVO[1]}));
                    }
                    else {
                        idleVO = nextVO;
                        PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(idleVO[1], 0, {prompt: idleVO[0]}));
                    }

                    HLF.Application.instance.voiceover.playVO(idleVO, {
                        mode: PlayMode.New,
                        callback: () => {
                            this.idleTimeline?.restart(true);
                        },
                        skipCC: true,
                        priority: -1,
                    });
                });
                this.idleTimeline.restart(true);
            });
        };

        if (TrashGameModel.round > 1) {
            _startSorting();
        }

        else {
            HLF.Application.instance.voiceover.playVO(VO.TB_SORT01, () => {
                this.bins[0].scaleTo(this.bins[0].scaleXY * 1.15, 0.15, Sine.easeInOut).delay(0.5);
                this.bins[0].Highlight();
                HLF.Application.instance.voiceover.playVO([0.5, VO.TB_SORT02], () => {
                    this.bins[0].scaleTo(this.bins[0].scaleXY / 1.15, 1, Elastic.easeOut);
                    this.bins[1].scaleTo(this.bins[1].scaleXY * 1.15, 0.15, Sine.easeInOut).delay(0.5);
                    this.bins[0].UnHighlight();
                    this.bins[1].Highlight();
                    HLF.Application.instance.voiceover.playVO([0.5, VO.TB_SORT03], () => {
                        this.bins[1].scaleTo(this.bins[1].scaleXY / 1.15, 1, Elastic.easeOut);
                        this.bins[2].scaleTo(this.bins[2].scaleXY * 1.15, 0.15, Sine.easeInOut).delay(0.5);
                        this.bins[1].UnHighlight();
                        this.bins[2].Highlight();
                        HLF.Application.instance.voiceover.playVO([0.5, VO.TB_SORT04], () => {
                            this.bins[2].scaleTo(this.bins[2].scaleXY / 1.15, 1, Elastic.easeOut);
                            this.bins[2].UnHighlight();
                            _startSorting();
                        });
                    });
                });
            });
        }
    }

    private throwTo(bin: TrashBin) {
        this.idleTimeline?.restart(true);
        const item = this.oscar.throwItem()!;
        if (item) {
            if (bin.trashType.includes(item.trashType)) {
                this.correctAnswerCounter++;
                if (this.correctAnswerCounter % 3 === 0) {

                    const nextVO = correctAnswerVOs();
                    let correctAnswerVO: string | string[];

                    if (nextVO instanceof Function) {
                        correctAnswerVO = nextVO(item);
                        PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(correctAnswerVO[1], 0, {trash: correctAnswerVO[0]}));
                    }
                    else {
                        correctAnswerVO = nextVO;
                        PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(correctAnswerVO));
                    }

                    HLF.Application.instance.voiceover.playVO(correctAnswerVO, {mode: PlayMode.Append, skipCC: true, callback: () => {
                        this.idleTimeline?.restart(true);
                    }});
                }
            }

            HLF.Application.instance.audio.stop("trash-blast-item-in-bin", HLF.AudioCategory.SFX);

            const itemClone = new Sprite(item.sprite.texture, {x: 0.5, y: 0.5});
            TweenLite.delayedCall(1.5, () => {
                itemClone.position.copyFrom(this.throwContainer.toLocal(this.oscar.nozzleWorldPosition));
                this.throwContainer.addChild(itemClone);

                itemClone.visible = false;
                itemClone.scale.set(0.25);
                itemClone.alpha = 0;
                itemClone.tweenTo(0.25, Ease.expoInOut, {
                    scaleXY: 3,
                    visible: true,
                    alpha: 1,
                    x: (bin.x + itemClone.x) / 2,
                    y: ((bin.y - bin.height) + itemClone.y) / 2,
                });
            });

            TweenLite.delayedCall(1.75, () => {

                if (bin.trashType.includes(item.trashType)) {
                    HLF.Application.instance.audio.play("correctanswer", 1, false, HLF.AudioCategory.SFX);
                }
                else {
                    HLF.Application.instance.audio.play("wronganswer", 1, false, HLF.AudioCategory.SFX);
                }

                itemClone.tweenTo(0.6, Ease.sineOut, {
                    x: bin.x,
                    y: bin.y - bin.height,
                    scaleXY: 1.5,
                    onComplete: () => {
                        this.idleTimeline?.restart(true);
                        if (bin.trashType.includes(item.trashType)) {
                            itemClone.tweenTo(0.4, Ease.backIn, {
                                y: bin.y - bin.height / 2,
                                alpha: 0,
                                onComplete: () => {
                                    this.idleTimeline?.restart(true);
                                    HLF.Application.instance.audio.play("trash-blast-item-in-bin", 1, false, HLF.AudioCategory.SFX);

                                    this.trackEvent("gameQuestionCA", {
                                        questionAsked: item.name.replace(/^\w+\-/, "").replace(/^\w/, (c) => c.toUpperCase()),
                                        questionNumber: (this.itemsReturned + 1),
                                        playerAnswer: bin.trashType.map((tt) => tt.replace(/^\w/, (c) => c.toUpperCase())).join(", "),
                                    });
                                    this.itemsReturned++;

                                    if (!this.oscar.loadItem()) {
                                        this.onAllItemsReturned();
                                    }
                                    else {
                                        this.wrongAnswerCounter = 0;
                                        if (this.itemsReturned % 3 === 0) {
                                            HLF.Application.instance.voiceover.playVO(VO.TB_SORT07, PlayMode.Append, () => {
                                                this.idleTimeline?.restart(true);
                                            });
                                        }
                                        // reset bins for next item
                                        this.bins.forEach((it) => {
                                            it.Enable();
                                            it.UnHighlight();
                                        });
                                    }

                                    item.destroy();
                                    itemClone.destroy();
                                },
                            });
                        } else {
                            this.wrongAnswerCounter++;
                            this.trackEvent("gameQuestionWA", {
                                questionAsked: item.name.replace(/^\w+\-/, "").replace(/^\w/, (c) => c.toUpperCase()),
                                questionNumber: (this.itemsReturned + 1),
                                playerAnswer: bin.trashType.map((tt) => tt.replace(/^\w/, (c) => c.toUpperCase())).join(", "),
                            });

                            let nozPos = this.throwContainer.toLocal(this.oscar.nozzleWorldPosition);
                            itemClone.tweenTo(0.25, Ease.sineIn, {
                                x: (bin.x + nozPos.x) / 2,
                                y: ((bin.y - bin.height) + nozPos.y) / 2,
                                scaleXY: 3,
                                alpha: 1,
                                onComplete: () => {
                                    this.idleTimeline?.restart(true);
                                    HLF.Application.instance.audio.play("trash-blast-item-in-bin", 1, false, HLF.AudioCategory.SFX);
                                    this.oscar.catchItem(item);
                                    nozPos = this.throwContainer.toLocal(this.oscar.nozzleWorldPosition);
                                    itemClone.tweenTo(0.6, Ease.expoInOut, {
                                        alpha: 0,
                                        scaleXY: 0,
                                        x: nozPos.x,
                                        y: nozPos.y,
                                        onComplete: () => {
                                            itemClone.destroy();
                                        },
                                    });
                                    bin.Disable();
                                    if (this.wrongAnswerCounter === 1) {
                                        let wrongAnswerVO: string;
                                        switch (item.trashType) {
                                            case TrashType.Plastic:
                                                wrongAnswerVO = VO.TB_SORT_WA01;
                                                break;
                                            case TrashType.Paper:
                                                wrongAnswerVO = VO.TB_SORT_WA02;
                                                break;
                                            case TrashType.Metal:
                                                wrongAnswerVO = VO.TB_SORT_WA03;
                                                break;
                                            case TrashType.Organic:
                                                wrongAnswerVO = VO.TB_SORT_WA04;
                                                break;
                                        }
                                        HLF.Application.instance.voiceover.playVO([item.voiceoverB, wrongAnswerVO], {skipCC: true, priority: 2, callback: () => {
                                            this.idleTimeline?.restart(true);
                                        }});
                                        PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(wrongAnswerVO, 0, {trashb: item.voiceoverB}));
                                    }
                                    else if (this.wrongAnswerCounter === 2) {
                                        const correctBin: TrashBin = this.bins.filter((b) => b.trashType.includes(item.trashType))[0]!;
                                        correctBin.pulse();
                                        correctBin.Highlight();
                                        HLF.Application.instance.voiceover.playVO(VO.TB_SORT_WA05, {priority: 2, callback: () => {
                                            this.idleTimeline?.restart(true);
                                            correctBin.stopPulse();
                                        }});
                                    }
                                },
                            });
                        }
                    },
                });
            });

        }
    }

    private onAllItemsReturned() {
        this.idleTimeline?.kill();
        this.idleTimeline?.clear();
        this.homeButton.visible = false;
        this.oscar.wait();
        // no more items to return
        this.bins.forEach((it) => {
            it.Enable();
            it.interactive = false;
            it.UnHighlight();
        });

        this.interactive = false;

        this.trackEvent("gameComponentComplete", {
            componentType: "Sort",
            componentName: "Sort Trash",
            duration: (new Date().valueOf() - this.screenStartTime) / 1000,
        });

        this.trackEvent("gameRoundComplete", {
            roundNumber: Application.instance.round,
            duration: (new Date().valueOf() - TrashGameModel.roundStartTime) / 1000,
        });

        if (LightsOutGameModel.round > Application.instance.playthrough && PlanterGameModel.GameState.round > Application.instance.playthrough && TrashGameModel.round === Application.instance.playthrough) {
            this.trackEvent("gamePlaythroughComplete", {
                duration: (new Date().valueOf() - Application.instance.playthroughStartTime) / 1000,
                playthroughNumber: Application.instance.playthrough,
            });
            Application.instance.playthrough++;
            Application.instance.playthroughStartTime = new Date().valueOf();
        }
        Application.instance.round++;
        TrashGameModel.round++;

        HLF.Application.instance.voiceover.playVO(TrashReturnScreen.allItemsReturnedVO(), HLF.PlayMode.Append, () => {
            this.oscar.visible = false;
            this.trackEvent("gameAchievement", {achievementType: "Trash Blaster Badge", roundNumber: (Application.instance.round - 1)});
            this._subscriptions.push(PubSub.subscribe("home button clicked", () => this.trackEvent("gameMenuClick", {buttonName: "Home"})));
            this._subscriptions.push(PubSub.subscribe("home button auto clicked", () => this.trackEvent("gameMenuClick", {buttonName: "AutoHome"})));
            this._subscriptions.push(PubSub.subscribe("blackout clicked", () => this.trackEvent("gameMenuClick", {buttonName: "NotHome"})));
            PubSub.publishSync(HLF.SHOW_POPUP, new HLF.PopupToken(TrashBadgePopup.NAME, this.onBadgePopupClosed.bind(this), true, false));
        });
    }
}
